import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Panel, Btn, Badge, Th, Td, inputCls, Field, Textarea } from "@/admin/ui";

const STATUSES = ["all", "new", "in_review", "closed"];

export default function Submissions() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/api/admin/enquiries", { params: { status, q } });
    setItems(data.items || []);
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  const setStatusFor = async (id, newStatus) => {
    try { await api.patch(`/api/admin/enquiries/${id}`, { status: newStatus }); toast.success("Status updated"); await load(); if (active?.id === id) setActive({ ...active, status: newStatus }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { const { data } = await api.patch(`/api/admin/enquiries/${active.id}`, { note }); setActive(data); setNote(""); toast.success("Note added"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/api/admin/enquiries/${id}`); toast.success("Deleted"); setActive(null); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-submissions">
      <h1 className="mb-6 text-2xl font-bold text-ink-heading">Submissions</h1>
      <Panel
        actions={
          <>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} w-auto`} data-testid="sub-filter-status">
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select>
            <input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} w-48`} data-testid="sub-search" />
          </>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100"><tr><Th>Ref</Th><Th>Type</Th><Th>Company</Th><Th>Name</Th><Th>Status</Th><Th>Received</Th><Th></Th></tr></thead>
            <tbody className="divide-y divide-slate-50">
              {items.map((it) => (
                <tr key={it.id} className="hover:bg-slate-50" data-testid={`sub-row-${it.ref}`}>
                  <Td className="font-mono text-xs">{it.ref}</Td>
                  <Td className="capitalize">{it.type}</Td>
                  <Td className="font-medium text-ink-heading">{it.company}</Td>
                  <Td>{it.name}</Td>
                  <Td><Badge status={it.status} /></Td>
                  <Td className="text-xs text-ink-muted">{(it.created_at || "").slice(0, 10)}</Td>
                  <Td><Btn variant="ghost" onClick={() => setActive(it)} data-testid={`sub-view-${it.ref}`}>View</Btn></Td>
                </tr>
              ))}
              {items.length === 0 && <tr><Td className="text-ink-muted" >No submissions found.</Td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setActive(null)}>
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="sub-detail">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-xs text-ink-muted">{active.ref}</p>
                <h2 className="text-xl font-semibold text-ink-heading">{active.company}</h2>
              </div>
              <Badge status={active.status} />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <p><span className="text-ink-muted">Name:</span> {active.name}</p>
              <p><span className="text-ink-muted">Type:</span> {active.type}</p>
              <p><span className="text-ink-muted">Email:</span> {active.email}</p>
              <p><span className="text-ink-muted">Country:</span> {active.country || "—"}</p>
              {active.licence ? <p><span className="text-ink-muted">Licence:</span> {active.licence}</p> : null}
              {active.capability ? <p><span className="text-ink-muted">Capability:</span> {active.capability}</p> : null}
              <p className="col-span-2"><span className="text-ink-muted">Routed to:</span> {active.routed_to || "—"} · {active.email_sent ? "email sent" : "email pending"}</p>
            </div>
            <div className="mt-4 rounded-lg bg-slate-50 p-4 text-sm text-ink-body whitespace-pre-wrap">{active.message}</div>

            <div className="mt-5 flex flex-wrap gap-2">
              {["new", "in_review", "closed"].map((s) => (
                <Btn key={s} variant={active.status === s ? "primary" : "ghost"} onClick={() => setStatusFor(active.id, s)} data-testid={`sub-status-${s}`}>{s.replace("_", " ")}</Btn>
              ))}
              <Btn variant="danger" className="ml-auto" onClick={() => remove(active.id)} data-testid="sub-delete">Delete</Btn>
            </div>

            <div className="mt-6">
              <h3 className="text-sm font-semibold text-ink-heading">Internal notes</h3>
              <div className="mt-2 space-y-2">
                {(active.notes || []).map((n, i) => (
                  <div key={i} className="rounded-lg border border-slate-100 p-3 text-sm">
                    <p className="text-ink-body">{n.text}</p>
                    <p className="mt-1 text-xs text-ink-muted">{n.by} · {(n.at || "").slice(0, 16).replace("T", " ")}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" data-testid="sub-note-input" />
                <Btn onClick={addNote} data-testid="sub-note-add">Add</Btn>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
